// navigation.store.ts
import { computed, ref } from "vue";
import type { Router } from "vue-router";
import { useMenuStore, type Menu } from "../stores/menu.store";
import { BreadcrumbItem, useBreadcrumbStore } from "./breadcrumb.store";

export interface NavItem {
    id: string;
    label: string;
    icon?: string;
    route?: string;
    section?: string;
    children?: NavItem[];
    chevron?: boolean;
}

export const activeId = ref<string>("");
export const sidebarCollapsed = ref<boolean>(false);
export const expanded = ref<Set<string>>(new Set());

function mapMenuItem(item: Record<string, any>): NavItem {
    return {
        id: item.key,
        label: item.label,
        icon: item.icon,
        route: item.route,
        children: Array.isArray(item.child) ? item.child.map(mapMenuItem) : undefined,
        chevron: Boolean(item.child?.length),
    };
}

export const getNavItems = (): NavItem[] => {
    const menuStore = useMenuStore();
    return menuStore.menu.map((item) => {
        const mapped = mapMenuItem(item);
        if (mapped.id === "dashboard") mapped.section = "MENU";
        return mapped;
    });
};

export const NAV = computed<NavItem[]>(() => getNavItems());

export function isActive(item: NavItem): boolean {
    return activeId.value === item.id;
}

export function isBranchActive(item: NavItem): boolean {
    if (!item.children) return false;
    return item.children.some((child) => child.id === activeId.value || isBranchActive(child));
}

function matchesRoute(itemRoute: string | undefined, currentPath: string): boolean {
    if (!itemRoute) return false;
    if (itemRoute === currentPath) return true;
    if (itemRoute === "/") return false;
    return currentPath.startsWith(itemRoute + "/");
}

function findItemByPath(
    items: NavItem[],
    path: string,
    parents: string[] = [],
): { item: NavItem; parents: string[] } | null {
    for (const item of items) {
        if (matchesRoute(item.route, path)) {
            return { item, parents };
        }

        if (item.children) {
            const found = findItemByPath(item.children, path, [...parents, item.id]);
            if (found) return found;
        }
    }

    return null;
}

export function syncActiveWithRoute(router: Router) {
    const currentPath = router.currentRoute.value.path;
    const items = NAV.value;

    const matched = findItemByPath(items, currentPath);

    if (matched) {
        activeId.value = matched.item.id;
        const menuStore = useMenuStore();
        menuStore.setActive(matched.item.id as Menu["key"]);
        matched.parents.forEach((parentId) => expanded.value.add(parentId));
        updateCrumbs(items, matched.item.id);
    }
}

export function updateCrumbs(
    items: NavItem[],
    targetId: string,
    trail: BreadcrumbItem[] = [],
): boolean {
    const breadcrumb = useBreadcrumbStore();
    for (const item of items) {
        const currentTrail = [...trail, { label: item.label, route: item.route }];

        if (item.id === targetId) {
            breadcrumb.setCrumbs(currentTrail);
            return true;
        }

        if (item.children && updateCrumbs(item.children, targetId, currentTrail)) {
            return true;
        }
    }
    return false;
}

export function onSelect(item: NavItem, router?: any, shouldOpen = false) {
    const menuStore = useMenuStore();

    if (item.children?.length) {
        if (expanded.value.has(item.id)) {
            if (shouldOpen) {
                return;
            }

            expanded.value.delete(item.id);
        } else {
            expanded.value.add(item.id);
        }

        return;
    }

    activeId.value = item.id;
    menuStore.setActive(item.id as Menu["key"]);

    if (item.route && router) {
        router.push(item.route);
    }
}
